import { ApiErrorEnum } from '../../enums/ApiErrorEnum';
import { getApiErrorCode } from '../apiErrorCode';

/** Human-readable 404 bodies for a blocked/missing child inside a folder share. */
const SHARED_FOLDER_ITEM_MESSAGES = new Set([
  'File not found in this shared folder',
  'Folder not found in this shared folder',
  'Folder not found',
  'File not found',
]);

function normalizeErrorString(value: string): string {
  return value.trim().replace(/^"+|"+$/g, '');
}

/** Axios / APIService error payload body (`response.data` or equivalent). */
export function getPublicLinkErrorPayload(error: unknown): unknown {
  const err = error as { response?: { data?: unknown } | unknown; data?: unknown };
  if (err?.response && typeof err.response === 'object' && err.response !== null) {
    const res = err.response as { data?: unknown };
    if ('data' in res) {
      return res.data;
    }
    return err.response;
  }
  return err?.data;
}

export function getPublicLinkHttpStatus(error: unknown): number | undefined {
  const err = error as { status?: number; response?: { status?: number } };
  return err?.status ?? err?.response?.status;
}

export function isPublicSharedFolderItemNotFound(error: unknown): boolean {
  const payload = getPublicLinkErrorPayload(error);
  if (typeof payload === 'string') {
    return SHARED_FOLDER_ITEM_MESSAGES.has(normalizeErrorString(payload));
  }
  const code = getApiErrorCode(payload);
  return code != null && SHARED_FOLDER_ITEM_MESSAGES.has(code);
}

export function isPublicLinkUnavailableCode(code: string | undefined): boolean {
  return code === ApiErrorEnum.LinkNotFound || code === ApiErrorEnum.LinkRevoked;
}

export type PublicLinkMappedError =
  | { kind: 'password' }
  | { kind: 'unavailable' }
  | { kind: 'itemNotFound' }
  | { kind: 'expired'; maxAccess: boolean }
  | { kind: 'watermarkDenied' }
  | { kind: 'unknown' };

/**
 * Maps guest public-link HTTP errors to UI outcomes (info / viewer / download).
 */
export function mapPublicLinkHttpError(error: unknown): PublicLinkMappedError {
  const status = getPublicLinkHttpStatus(error);
  const payload = getPublicLinkErrorPayload(error);
  const code = getApiErrorCode(payload);

  if (status === 401) {
    return { kind: 'password' };
  }
  if (code === ApiErrorEnum.WatermarkProtectionDownloadDenied) {
    return { kind: 'watermarkDenied' };
  }
  if (status === 404) {
    if (isPublicSharedFolderItemNotFound(error)) {
      return { kind: 'itemNotFound' };
    }
    return { kind: 'unavailable' };
  }
  if (status === 410) {
    return {
      kind: 'expired',
      maxAccess: code === ApiErrorEnum.MaxAccessReached,
    };
  }
  if (isPublicLinkUnavailableCode(code)) {
    return { kind: 'unavailable' };
  }
  return { kind: 'unknown' };
}
